import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useAuthSession } from 'modules/auth-session';

import { useAddManualPaymentMutation, useUpdatedManualPaymentMutation } from '../api/payment-methods.api';
import { setIsManualPaymentLoading, setManualPaymentState, setPaymentSettings } from '../store/payment-method.slice';
import {
  AddManualPaymentPayload,
  EPaymentMethodsState,
  UpdateManualPaymentPayload,
} from '../types/payment-methods.type';

interface UsePaymentMethods {
  addManualPaymentHandler: (payload: AddManualPaymentPayload) => Promise<void>;
  updateManualPaymentHandler: (payload: UpdateManualPaymentPayload) => Promise<void>;
}

export const usePaymentMethods = (): UsePaymentMethods => {
  const dispatch = useAppDispatch();

  const { checkSessionHandler } = useAuthSession();

  const [addManualPaymentQuery] = useAddManualPaymentMutation();
  const [updateManualPaymentQuery] = useUpdatedManualPaymentMutation();

  const addManualPaymentHandler = async (payload: AddManualPaymentPayload) => {
    dispatch(setIsManualPaymentLoading(true));
    const { data: response, error } = await addManualPaymentQuery(payload);
    await checkSessionHandler();
    dispatch(setIsManualPaymentLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to add Payment by Invoice!');
      return;
    }
    toast.success('Payment by Invoice added!');
    batch(() => {
      dispatch(setManualPaymentState(EPaymentMethodsState.INIT));
      dispatch(setPaymentSettings(response.data));
    });
  };

  const updateManualPaymentHandler = async (payload: UpdateManualPaymentPayload) => {
    dispatch(setIsManualPaymentLoading(true));
    const { data: response, error } = await updateManualPaymentQuery(payload);
    dispatch(setIsManualPaymentLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update Payment by Invoice!');
      return;
    }
    toast.success('Payment by Invoice updated!');
    batch(() => {
      dispatch(setManualPaymentState(EPaymentMethodsState.INIT));
      dispatch(setPaymentSettings(response.data));
    });
  };

  return {
    addManualPaymentHandler,
    updateManualPaymentHandler,
  };
};
